/**
 * Service Layer Auth Provider
 *
 * Handles authentication via Service Layer cookie-based OAuth flow.
 * The heavy lifting (the two checkAuthStatus closures + re-handoff
 * logic) lives in sibling modules:
 *   - authCheckStatus: runOAuthCallbackCheck + runMountSessionCheck
 *   - authReHandoff: attemptReHandoff (Supabase -> SL cookies)
 *   - authSession / authActions / authOAuth / authDebug: existing
 */

import React, { useEffect, useMemo, useState, useRef } from 'react'
import { ServiceLayerAuthContext } from './authContext'
import type { ServiceLayerAuthContextValue } from './authTypes'
import { createSafeFetchSession, createSanitizeUrl } from './authSession'
import { createSignIn, createSignOut } from './authActions'
import { setupAuthDebug } from './authDebug'
import { handleOAuthError } from './authOAuth'
import { runMountSessionCheck, runOAuthCallbackCheck } from './authCheckStatus'

export type { ServiceLayerAuthContextValue, SessionResponse } from './authTypes'
export { useServiceLayerAuth } from './authContext'
export { apiFetch, apiFetchWithAutoRetry, apiJson } from './apiHelpers'

export const ServiceLayerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const env = import.meta.env
  const TEST_MODE = !!(env.VITE_TEST_MODE === 'true' || env.VITE_MOCK_AUTH === 'true' || (typeof window !== 'undefined' && (window as Window & { __TEST_MODE__?: boolean }).__TEST_MODE__))
  // Local dev bypass: cookie-based auth cannot work from localhost
  // (cross-origin cookie restriction). Use VITE_FORCE_REAL_AUTH=true
  // to disable the bypass when testing against real auth.
  const IS_DEV = !!(env.DEV || env.MODE === 'development') && env.VITE_FORCE_REAL_AUTH !== 'true'

  const [email, setEmail] = useState<string | null>(IS_DEV ? 'dev@clanda.de' : null)
  const [role, setRole] = useState<string | null>(IS_DEV ? 'superadmin' : null)
  const [loading, setLoading] = useState<boolean>(false)
  const [ready, setReady] = useState<boolean>(IS_DEV)
  const [authError, setAuthError] = useState<string | null>(null)
  const [lastErrorCode, setLastErrorCode] = useState<string | null>(null)
  const [csrfToken, setCsrfToken] = useState<string | null>(null)

  // Prevent double session refresh races (background interval +
  // visibility change + initial check).
  const inFlightRef = useRef<boolean>(false)

  // Track current email without triggering effect re-runs.
  const emailRef = useRef<string | null>(IS_DEV ? 'dev@clanda.de' : null)

  // Debounce sanitizeUrl across rapid mounts (SPA transitions).
  const sanitizeUrlTimeoutRef = useRef<number | null>(null)

  const sessionState = useMemo(() => ({ setEmail, setRole, setCsrfToken }), [])

  const safeFetchSession = useMemo(
    () => createSafeFetchSession(sessionState, inFlightRef),
    [sessionState],
  )

  const sanitizeUrl = useMemo(() => createSanitizeUrl(sanitizeUrlTimeoutRef), [])

  // Test mode: provide mock email for local dev.
  useEffect(() => {
    if (TEST_MODE) setEmail('test@clanda.de')
  }, [TEST_MODE])

  // Keep ref in sync for the auth effect (avoids email in deps).
  useEffect(() => {
    emailRef.current = email
  }, [email])

  // Check auth status on mount and after OAuth callback.
  useEffect(() => {
    if (TEST_MODE) return
    if (IS_DEV) return

    const ac = new AbortController()
    const urlParams = new URLSearchParams(window.location.search)
    const authStatus = urlParams.get('auth')
    const error = urlParams.get('error')

    const checkDeps = {
      safeFetchSession,
      setAuthError,
      setLastErrorCode,
      setLoading,
      setReady,
      setEmail,
      setRole,
      setCsrfToken,
    }

    if (authStatus === 'success') {
      sanitizeUrl()
      sessionStorage.removeItem('oauth_attempt_ts')
      setAuthError(null)
      setLastErrorCode(null)

      void runOAuthCallbackCheck({ ...checkDeps, signal: ac.signal })
      return () => { ac.abort() }
    }

    if (error) {
      handleOAuthError(error, urlParams, {
        setAuthError,
        setLastErrorCode,
        setEmail,
        setRole,
        setCsrfToken,
        setLoading,
        setReady,
        sanitizeUrl,
      })
      return
    }

    sessionStorage.removeItem('oauth_attempt_ts')

    const runCheck = (signal?: AbortSignal) =>
      runMountSessionCheck({ ...checkDeps, signal, ready, emailRef })

    void runCheck(ac.signal)

    // Background session refresh: every 10 minutes plus on tab
    // visibility change. Keeps UX snappy and prefetches a fresh
    // CSRF token if it rotates.
    const SESSION_REFRESH_INTERVAL_MS = 10 * 60 * 1000

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !ac.signal.aborted) {
        runCheck(ac.signal).catch(() => {
          // Silently fail — background refresh shouldn't disrupt UX
        })
      }
    }

    const intervalId = window.setInterval(() => {
      if (!ac.signal.aborted) {
        runCheck(ac.signal).catch(() => {
          // Silently fail — background refresh shouldn't disrupt UX
        })
      }
    }, SESSION_REFRESH_INTERVAL_MS)

    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      ac.abort()
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (sanitizeUrlTimeoutRef.current !== null) {
        window.clearTimeout(sanitizeUrlTimeoutRef.current)
        sanitizeUrlTimeoutRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- email/ready tracked via ref to prevent re-fire loop
  }, [safeFetchSession, sanitizeUrl, TEST_MODE])

  const authActionsState = useMemo(() => ({
    setEmail,
    setRole,
    setCsrfToken,
    setAuthError,
    setLastErrorCode,
    setLoading,
  }), [])

  const signIn = useMemo(
    () => createSignIn(authActionsState, TEST_MODE),
    [authActionsState, TEST_MODE],
  )

  const signOut = useMemo(
    () => createSignOut(authActionsState, TEST_MODE),
    [authActionsState, TEST_MODE],
  )

  // E2E test hooks (only in dev/test builds).
  useEffect(() => {
    const cleanup = setupAuthDebug(
      {
        email,
        ready,
        loading,
        csrfToken,
        authError,
        lastErrorCode,
        setEmail,
        setRole,
        setCsrfToken,
        setAuthError,
        setLastErrorCode,
      },
      TEST_MODE,
    )
    return cleanup
  }, [email, ready, loading, csrfToken, authError, lastErrorCode, TEST_MODE])

  const refreshSession = React.useCallback(async () => {
    const ac = new AbortController()
    try {
      await safeFetchSession(ac.signal)
    } finally {
      ac.abort()
    }
  }, [safeFetchSession])

  const isAuthenticated = email !== null

  const value = useMemo<ServiceLayerAuthContextValue>(
    () => ({
      isConfigured: true,
      email,
      role,
      loading,
      ready,
      isAuthenticated,
      signIn,
      signOut,
      refreshSession,
      authError,
      lastErrorCode,
      csrfToken,
    }),
    [email, role, loading, ready, isAuthenticated, signIn, signOut, refreshSession, authError, lastErrorCode, csrfToken],
  )

  return (
    <ServiceLayerAuthContext.Provider value={value}>{children}</ServiceLayerAuthContext.Provider>
  )
}

export default ServiceLayerAuthProvider
